import { LightBox } from '../../components/light-box';
import placements from '../../placements';
import { setFrequencyCapping } from '../../utils/frequency-capping';
import { Logger } from '../../utils/logger';
import { openURLbyTarget } from '../../utils/open-url-by-target';
import type { TrackingLibrary } from '../../utils/tracking';
import { trackEvent } from '../../utils/tracking';
import withBraze from '../../utils/with-braze';

const PLACEMENT = placements.PRESCREEN;

export interface PrescreenPayloadButton {
  id: string;
  text: string;
  clickAction: string;
  uri: string;
}

export interface PrescreenPayload {
  buttons: PrescreenPayloadButton[];
  openTarget?: string;
  message?: string;
  campaignId: string;
  messageUrl: string;
  messageClickAction: string;
  buttonColor?: string;
  imageUrl: string;
  altImage?: string;
  hasButton: boolean;
  isHTML: boolean;
  mobileImageURL?: string;
  removeCrossIcon?: boolean;
  template?: string;
  buttonText?: string;
  removeCloseButton?: boolean;
  button1AriaLabel?: string;
  button2AriaLabel?: string;
  button1Color?: string;
  button2Color?: string;
  title?: string;
}

interface PrescreenProps {
  close: () => void;
  payload: PrescreenPayload;
  trackingLibrary?: TrackingLibrary;
  section: string;
}

export interface BrazeUserAttribute {
  key: string;
  value: string | number;
}

export interface BrazeCustomEvent {
  name: string;
  properties?: any;
}

const Prescreen = ({
  close,
  payload,
  section,
  trackingLibrary,
}: PrescreenProps): JSX.Element => {
  Logger.log('PrescreenConsumer - rendering Message:', payload);

  const openURLbyClickAction = (url: string, clickAction: string): void => {
    if (clickAction !== 'URI' && !url) return close();

    openURLbyTarget(url, payload.openTarget);
    close();
  };

  const onURLButtonClick = (
    url: string,
    target = '_blank',
    buttonId = 'test_button'
  ): void => {
    trackingLibrary?.trackInAppMessageHTMLClick(
      PLACEMENT,
      payload.campaignId,
      buttonId
    );
    openURLbyTarget(url, target);
    close();
  };

  const onPrescreenClick = (): void => {
    trackingLibrary?.trackInAppMessageClick(PLACEMENT, payload.campaignId);
    openURLbyClickAction(payload.messageUrl, payload.messageClickAction);
  };

  const onPrescreenButtonClick = (): void => {
    trackingLibrary?.trackInAppMessageButtonClick(
      PLACEMENT,
      payload.campaignId
    );
    payload.buttons.forEach((button) => {
      openURLbyClickAction(button.uri || '', button.clickAction);
    });
  };

  const onPrescreenContentShown = (): void => {
    trackingLibrary?.trackInAppMessageImpression(PLACEMENT, payload.campaignId);
    setFrequencyCapping(section, PLACEMENT);
  };

  const onPrescreenClose = (): void => {
    trackEvent('brazeClose', {
      brazePlacement: 'prescreen',
      brazeTriggerID: payload?.campaignId,
    });
    close();
  };

  const setBrazeCustomAttribute = (payload: BrazeUserAttribute): void => {
    trackingLibrary?.setBrazeCustomAttribute(payload);
  };

  const sendBrazeCustomEvent = (payload: BrazeCustomEvent): void => {
    trackingLibrary?.sendBrazeCustomEvent(payload);
  };

  return (
    <LightBox
      onClose={onPrescreenClose}
      onPrescreenClick={onPrescreenClick}
      onPrescreenButtonClick={onPrescreenButtonClick}
      data={payload}
      onContentShown={onPrescreenContentShown}
      onURLButtonClick={onURLButtonClick}
      setBrazeCustomAttribute={setBrazeCustomAttribute}
      sendBrazeCustomEvent={sendBrazeCustomEvent}
    />
  );
};

const PrescreenConsumer = withBraze(PLACEMENT)(Prescreen);
export { PrescreenConsumer, Prescreen };
